import React, { useEffect, useState } from "react";
import {
  BookOpen,
  Briefcase,
  CheckCircle,
  Edit2,
  Filter,
  Layers,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  XCircle,
} from "lucide-react";
import { MasterProgramItem, programService } from "../services/programService";
import { departmentMasterService, MasterDepartmentItem } from "../services/departmentMasterService";
import { universityService, UniversityItem } from "../services/universityService";
import { institutionTypeService, InstitutionTypeItem } from "../services/institutionTypeService";
import { academicPatternService, MasterAcademicPatternItem } from "../services/academicPatternService";


const DEGREE_LEVELS = [
  { value: "Certificate", label: "Certificate" },
  { value: "Diploma", label: "Diploma" },
  { value: "UG", label: "Undergraduate (UG)" },
  { value: "PG", label: "Postgraduate (PG)" },
  { value: "Doctorate", label: "Doctorate" },
];

const PROGRAM_CATEGORIES = [
  "Engineering",
  "Science",
  "Commerce",
  "Arts",
  "Medical",
  "Law",
  "Management",
  "Education",
  "Pharmacy",
  "Agriculture",
  "Architecture",
  "Design",
  "Vocational",
];

const ACADEMIC_PATTERNS = ["Annual", "Semester", "Trimester", "CBCS", "NEP 2020"];

const STANDARD_PRESETS = [
  {
    name: "B.Tech Computer Engineering",
    short_name: "B.Tech Comp",
    program_code: "BTCE",
    degree_level: "UG",
    program_category: "Engineering",
    duration_years: 4,
    total_semesters: 8,
    total_credits: 160,
    academic_pattern: "CBCS",
    approval_body: "AICTE / UGC",
    dept_code: "COMP_ENG",
  },
  {
    name: "B.Tech Information Technology",
    short_name: "B.Tech IT",
    program_code: "BTIT",
    degree_level: "UG",
    program_category: "Engineering",
    duration_years: 4,
    total_semesters: 8,
    total_credits: 160,
    academic_pattern: "CBCS",
    approval_body: "AICTE / UGC",
    dept_code: "IT_ENG",
  },
  {
    name: "B.Tech Artificial Intelligence & Data Science",
    short_name: "B.Tech AI & DS",
    program_code: "BTAI",
    degree_level: "UG",
    program_category: "Engineering",
    duration_years: 4,
    total_semesters: 8,
    total_credits: 160,
    academic_pattern: "NEP 2020",
    approval_body: "AICTE / UGC",
    dept_code: "AIDS_ENG",
  },
  {
    name: "B.Tech Civil Engineering",
    short_name: "B.Tech Civil",
    program_code: "BTCIVIL",
    degree_level: "UG",
    program_category: "Engineering",
    duration_years: 4,
    total_semesters: 8,
    total_credits: 160,
    academic_pattern: "CBCS",
    approval_body: "AICTE",
    dept_code: "CIVIL_ENG",
  },
  {
    name: "B.Tech Mechanical Engineering",
    short_name: "B.Tech Mech",
    program_code: "BTMECH",
    degree_level: "UG",
    program_category: "Engineering",
    duration_years: 4,
    total_semesters: 8,
    total_credits: 160,
    academic_pattern: "CBCS",
    approval_body: "AICTE",
    dept_code: "MECH_ENG",
  },
  {
    name: "Diploma Computer Engineering",
    short_name: "Diploma Comp",
    program_code: "DIPCOMP",
    degree_level: "Diploma",
    program_category: "Engineering",
    duration_years: 3,
    total_semesters: 6,
    total_credits: 120,
    academic_pattern: "Semester",
    approval_body: "DTE",
    dept_code: "COMP_ENG",
  },
  {
    name: "M.Tech Computer Engineering",
    short_name: "M.Tech Comp",
    program_code: "MTCOMP",
    degree_level: "PG",
    program_category: "Engineering",
    duration_years: 2,
    total_semesters: 4,
    total_credits: 80,
    academic_pattern: "CBCS",
    approval_body: "AICTE",
    dept_code: "COMP_ENG",
  },
  {
    name: "B.Sc Physics",
    short_name: "B.Sc Phy",
    program_code: "BSCPHY",
    degree_level: "UG",
    program_category: "Science",
    duration_years: 3,
    total_semesters: 6,
    total_credits: 120,
    academic_pattern: "CBCS",
    approval_body: "UGC",
    dept_code: "PHYS_SCI",
  },
  {
    name: "B.Sc Chemistry",
    short_name: "B.Sc Chem",
    program_code: "BSCCHEM",
    degree_level: "UG",
    program_category: "Science",
    duration_years: 3,
    total_semesters: 6,
    total_credits: 120,
    academic_pattern: "CBCS",
    approval_body: "UGC",
    dept_code: "CHEM_SCI",
  },
  {
    name: "B.Sc Mathematics",
    short_name: "B.Sc Maths",
    program_code: "BSCMATH",
    degree_level: "UG",
    program_category: "Science",
    duration_years: 3,
    total_semesters: 6,
    total_credits: 120,
    academic_pattern: "CBCS",
    approval_body: "UGC",
    dept_code: "MATH_SCI",
  },
  {
    name: "M.Sc Physics",
    short_name: "M.Sc Phy",
    program_code: "MSCPHY",
    degree_level: "PG",
    program_category: "Science",
    duration_years: 2,
    total_semesters: 4,
    total_credits: 80,
    academic_pattern: "CBCS",
    approval_body: "UGC",
    dept_code: "PHYS_SCI",
  },
  {
    name: "Bachelor of Commerce",
    short_name: "B.Com",
    program_code: "BCOM",
    degree_level: "UG",
    program_category: "Commerce",
    duration_years: 3,
    total_semesters: 6,
    total_credits: 120,
    academic_pattern: "NEP 2020",
    approval_body: "UGC",
    dept_code: "ACCT_COMM",
  },
  {
    name: "Master of Commerce",
    short_name: "M.Com",
    program_code: "MCOM",
    degree_level: "PG",
    program_category: "Commerce",
    duration_years: 2,
    total_semesters: 4,
    total_credits: 80,
    academic_pattern: "CBCS",
    approval_body: "UGC",
    dept_code: "FIN_COMM",
  },
  {
    name: "Bachelor of Business Administration",
    short_name: "BBA",
    program_code: "BBA",
    degree_level: "UG",
    program_category: "Management",
    duration_years: 3,
    total_semesters: 6,
    total_credits: 120,
    academic_pattern: "CBCS",
    approval_body: "UGC",
    dept_code: "BUS_ADMIN",
  },
  {
    name: "Master of Business Administration",
    short_name: "MBA",
    program_code: "MBA",
    degree_level: "PG",
    program_category: "Management",
    duration_years: 2,
    total_semesters: 4,
    total_credits: 90,
    academic_pattern: "CBCS",
    approval_body: "AICTE",
    dept_code: "MBA_MGMT",
  },
  {
    name: "BA English",
    short_name: "BA Eng",
    program_code: "BAENG",
    degree_level: "UG",
    program_category: "Arts",
    duration_years: 3,
    total_semesters: 6,
    total_credits: 120,
    academic_pattern: "Semester",
    approval_body: "UGC",
    dept_code: "ENG_ARTS",
  },
  {
    name: "BA History",
    short_name: "BA Hist",
    program_code: "BAHIST",
    degree_level: "UG",
    program_category: "Arts",
    duration_years: 3,
    total_semesters: 6,
    total_credits: 120,
    academic_pattern: "Semester",
    approval_body: "UGC",
    dept_code: "HIST_ARTS",
  },
  {
    name: "MA English",
    short_name: "MA Eng",
    program_code: "MAENG",
    degree_level: "PG",
    program_category: "Arts",
    duration_years: 2,
    total_semesters: 4,
    total_credits: 80,
    academic_pattern: "Semester",
    approval_body: "UGC",
    dept_code: "ENG_ARTS",
  },
  {
    name: "Bachelor of Pharmacy",
    short_name: "B.Pharm",
    program_code: "BPHARM",
    degree_level: "UG",
    program_category: "Pharmacy",
    duration_years: 4,
    total_semesters: 8,
    total_credits: 160,
    academic_pattern: "CBCS",
    approval_body: "PCI / AICTE",
    dept_code: "PHARM_MED",
  },
  {
    name: "Diploma in Pharmacy",
    short_name: "D.Pharm",
    program_code: "DPHARM",
    degree_level: "Diploma",
    program_category: "Pharmacy",
    duration_years: 2,
    total_semesters: 4,
    total_credits: 80,
    academic_pattern: "Annual",
    approval_body: "PCI",
    dept_code: "PHARM_MED",
  },
  {
    name: "B.Sc Nursing",
    short_name: "B.Sc Nurs",
    program_code: "BSCNURS",
    degree_level: "UG",
    program_category: "Medical",
    duration_years: 4,
    total_semesters: 8,
    total_credits: 160,
    academic_pattern: "Semester",
    approval_body: "INC",
    dept_code: "NURS_MED",
  },
  {
    name: "Bachelor of Laws",
    short_name: "LLB",
    program_code: "LLB",
    degree_level: "UG",
    program_category: "Law",
    duration_years: 3,
    total_semesters: 6,
    total_credits: 120,
    academic_pattern: "Semester",
    approval_body: "BCI",
    dept_code: "LLB_LAW",
  },
  {
    name: "Master of Laws",
    short_name: "LLM",
    program_code: "LLM",
    degree_level: "PG",
    program_category: "Law",
    duration_years: 2,
    total_semesters: 4,
    total_credits: 80,
    academic_pattern: "Semester",
    approval_body: "BCI",
    dept_code: "LLM_LAW",
  },
];

export const ProgramManagementPage: React.FC = () => {
  const [programs, setPrograms] = useState<MasterProgramItem[]>([]);
  const [departments, setDepartments] = useState<MasterDepartmentItem[]>([]);
  const [universities, setUniversities] = useState<UniversityItem[]>([]);
  const [institutionTypes, setInstitutionTypes] = useState<InstitutionTypeItem[]>([]);
  const [academicPatterns, setAcademicPatterns] = useState<MasterAcademicPatternItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDegree, setSelectedDegree] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedPattern, setSelectedPattern] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [selectedInstType, setSelectedInstType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<MasterProgramItem | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    department: "",
    name: "",
    short_name: "",
    program_code: "",
    degree_level: "UG",
    program_category: "Engineering",
    duration_years: 3,
    total_semesters: 6,
    total_credits: 120,
    academic_pattern: "Semester",
    academic_pattern_ref: "",
    approval_body: "",
    description: "",
    institution_type: "",
    university: "",
    is_active: true,
  });

  useEffect(() => {
    loadAuxiliaryData();
  }, []);

  useEffect(() => {
    fetchPrograms();
  }, [
    searchTerm,
    selectedDegree,
    selectedCategory,
    selectedPattern,
    selectedDepartment,
    selectedUniversity,
    selectedInstType,
    selectedStatus,
  ]);

  const loadAuxiliaryData = async () => {
    try {
      const [deptRes, univRes, itRes, patRes] = await Promise.all([
        departmentMasterService.getDepartments({ is_active: true }),
        universityService.getUniversities(),
        institutionTypeService.getInstitutionTypes(),
        academicPatternService.getAcademicPatterns({ is_active: true }),
      ]);
      const depts = Array.isArray(deptRes) ? deptRes : deptRes.results || [];
      const univs = Array.isArray(univRes) ? univRes : (univRes as any).results || [];
      const itypes = Array.isArray(itRes) ? itRes : (itRes as any).results || [];
      const pats = Array.isArray(patRes) ? patRes : (patRes as any).results || [];

      setDepartments(depts);
      setUniversities(univs);
      setInstitutionTypes(itypes);
      setAcademicPatterns(pats);

      if (depts.length > 0) {
        setFormData((prev) => ({
          ...prev,
          department: depts[0].id,
          academic_pattern_ref: pats.find((p: any) => p.is_default)?.id || pats[0]?.id || "",
        }));
      }
    } catch (err) {
      console.error("Failed to load reference data", err);
    }
  };


  const fetchPrograms = async () => {
    setLoading(true);
    try {
      const res = await programService.getPrograms({
        search: searchTerm || undefined,
        degree_level: selectedDegree || undefined,
        program_category: selectedCategory || undefined,
        academic_pattern: selectedPattern || undefined,
        department: selectedDepartment || undefined,
        university: selectedUniversity || undefined,
        institution_type: selectedInstType || undefined,
        is_active: selectedStatus === "active" ? true : selectedStatus === "inactive" ? false : undefined,
      });
      const items = Array.isArray(res) ? res : res.results || [];
      setPrograms(items);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to load programs", err);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedDegree("");
    setSelectedCategory("");
    setSelectedPattern("");
    setSelectedDepartment("");
    setSelectedUniversity("");
    setSelectedInstType("");
    setSelectedStatus("");
  };

  const openCreateModal = () => {
    setEditingProgram(null);
    const defaultPat = academicPatterns.find((p) => p.is_default)?.id || academicPatterns[0]?.id || "";
    setFormData({
      department: departments[0]?.id || "",
      name: "",
      short_name: "",
      program_code: "",
      degree_level: "UG",
      program_category: "Engineering",
      duration_years: 3,
      total_semesters: 6,
      total_credits: 120,
      academic_pattern: "Semester",
      academic_pattern_ref: defaultPat,
      approval_body: "",
      description: "",
      institution_type: "",
      university: "",
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (prog: MasterProgramItem) => {
    setEditingProgram(prog);
    setFormData({
      department: prog.department,
      name: prog.name,
      short_name: prog.short_name || "",
      program_code: prog.program_code,
      degree_level: prog.degree_level,
      program_category: prog.program_category,
      duration_years: prog.duration_years,
      total_semesters: prog.total_semesters,
      total_credits: prog.total_credits || 120,
      academic_pattern: prog.academic_pattern,
      academic_pattern_ref: (prog as any).academic_pattern_ref || "",
      approval_body: prog.approval_body || "",
      description: prog.description || "",
      institution_type: prog.institution_type || "",
      university: prog.university || "",
      is_active: prog.is_active,
    });
    setIsModalOpen(true);
  };


  const handleSelectPreset = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = STANDARD_PRESETS.find((p) => p.program_code === e.target.value);
    if (selected) {
      const matchedDept = departments.find((d) => d.code === selected.dept_code) || departments[0];
      setFormData((prev) => ({
        ...prev,
        name: selected.name,
        short_name: selected.short_name,
        program_code: selected.program_code,
        degree_level: selected.degree_level as any,
        program_category: selected.program_category as any,
        duration_years: selected.duration_years,
        total_semesters: selected.total_semesters,
        total_credits: selected.total_credits,
        academic_pattern: selected.academic_pattern as any,
        approval_body: selected.approval_body,
        department: matchedDept ? matchedDept.id : prev.department,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingProgram) {
        await programService.updateProgram(editingProgram.id, formData as any);
      } else {
        await programService.createProgram(formData as any);
      }
      setIsModalOpen(false);
      fetchPrograms();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to save program.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await programService.deleteProgram(id);
      setDeletingId(null);
      fetchPrograms();
    } catch (err: any) {
      alert("Failed to delete program.");
    }
  };

  // Pagination Math
  const totalPages = Math.ceil(programs.length / pageSize) || 1;
  const paginatedPrograms = programs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="space-y-6 font-sans text-slate-100">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900/90 border border-slate-800 p-5 rounded-2xl shadow-xl">
        <div className="space-y-1">
          <h1 className="text-xl font-bold text-slate-100 flex items-center gap-2.5">
            <BookOpen className="w-6 h-6 text-indigo-400" />
            Program (Course) Master
          </h1>
          <p className="text-xs text-slate-400">
            Centralized academic program catalog mapping University → Department → Program structure.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPrograms}
            className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors border border-slate-700"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl flex items-center gap-2 shadow-lg transition-all"
          >
            <Plus className="w-4 h-4" /> Add Program
          </button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-2xl space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {/* Search Box */}
          <div className="relative col-span-1 sm:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" />
            <input
              type="text"
              placeholder="Search program name, code, description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
            />
          </div>

          {/* Department Filter */}
          <select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-slate-300 focus:outline-none"
          >
            <option value="">-- All Departments --</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.code})
              </option>
            ))}
          </select>

          {/* Degree Level Filter */}
          <select
            value={selectedDegree}
            onChange={(e) => setSelectedDegree(e.target.value)}
            className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-slate-300 focus:outline-none"
          >
            <option value="">-- All Degree Levels --</option>
            {DEGREE_LEVELS.map((lvl) => (
              <option key={lvl.value} value={lvl.value}>
                {lvl.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3 pt-2 border-t border-slate-800/80">
          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none"
          >
            <option value="">All Categories</option>
            {PROGRAM_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          {/* Pattern Filter */}
          <select
            value={selectedPattern}
            onChange={(e) => setSelectedPattern(e.target.value)}
            className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none"
          >
            <option value="">All Academic Patterns</option>
            {ACADEMIC_PATTERNS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          {/* University Filter */}
          <select
            value={selectedUniversity}
            onChange={(e) => setSelectedUniversity(e.target.value)}
            className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none"
          >
            <option value="">All Universities</option>
            {universities.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none"
          >
            <option value="">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>

          {/* Reset Filters */}
          <button
            onClick={resetFilters}
            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-xl text-xs flex items-center justify-center gap-1.5 transition-colors"
          >
            <Filter className="w-3.5 h-3.5" /> Clear Filters
          </button>
        </div>
      </div>

      {/* Program Grid */}
      {loading ? (
        <div className="p-12 text-center text-xs text-slate-400 bg-slate-900/50 rounded-2xl border border-slate-800">
          Loading Program Master...
        </div>
      ) : paginatedPrograms.length === 0 ? (
        <div className="p-12 text-center space-y-2 bg-slate-900/50 rounded-2xl border border-slate-800">
          <p className="text-sm font-bold text-slate-300">No Academic Programs Found</p>
          <p className="text-xs text-slate-500">Try clearing filters or add a new Program.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedPrograms.map((prog) => (
            <div
              key={prog.id}
              className="p-5 bg-slate-900/90 border border-slate-800 rounded-2xl space-y-3 shadow-md hover:border-indigo-500/40 transition-all flex flex-col justify-between"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-indigo-950 text-indigo-300 border border-indigo-800 rounded">
                    {prog.program_code}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-cyan-950 text-cyan-300 border border-cyan-800 rounded">
                      {prog.degree_level}
                    </span>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                        prog.is_active
                          ? "bg-emerald-950 text-emerald-300 border-emerald-800"
                          : "bg-rose-950 text-rose-300 border-rose-800"
                      }`}
                    >
                      {prog.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-slate-100 text-sm">{prog.name}</h3>
                  {prog.short_name && <p className="text-[11px] text-slate-400 font-mono">({prog.short_name})</p>}
                </div>

                <div className="text-xs text-slate-400 space-y-1 pt-1 border-t border-slate-800/80">
                  <p className="flex justify-between">
                    <span>Department:</span>
                    <span className="font-medium text-slate-200">{prog.department_name || "Academic Department"}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Category:</span>
                    <span className="text-slate-300">{prog.program_category}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Pattern:</span>
                    <span className="text-indigo-400 font-mono">{prog.academic_pattern}</span>
                  </p>
                  {prog.approval_body && (
                    <p className="flex justify-between text-[11px]">
                      <span>Approval Body:</span>
                      <span className="text-amber-300 font-mono">{prog.approval_body}</span>
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-3 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-400">
                <span>
                  {prog.duration_years} Yrs ({prog.total_semesters} Semesters) • {prog.total_credits || 120} Cr
                </span>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditModal(prog)}
                    className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                    title="Edit Program"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setDeletingId(prog.id)}
                    className="p-1.5 bg-rose-950/60 hover:bg-rose-900 text-rose-300 rounded-lg transition-colors"
                    title="Delete Program"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-slate-900/90 border border-slate-800 p-4 rounded-2xl text-xs text-slate-400">
          <span>
            Page <strong className="text-slate-200">{currentPage}</strong> of{" "}
            <strong className="text-slate-200">{totalPages}</strong> ({programs.length} total programs)
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-50 rounded-xl font-semibold transition-colors"
            >
              Previous
            </button>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-50 rounded-xl font-semibold transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-xl bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-2xl my-8"
          >
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-base font-bold text-slate-100 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-indigo-400" />
                {editingProgram ? "Edit Program Master" : "Create New Program Master"}
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {/* Standard Preset Picker */}
            {!editingProgram && (
              <div className="p-3 bg-indigo-950/40 border border-indigo-800/60 rounded-xl space-y-1.5">
                <label className="block text-[11px] font-bold text-indigo-300 flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5 text-amber-400" /> Auto-fill from Standard Degree Preset
                </label>
                <select
                  onChange={handleSelectPreset}
                  defaultValue=""
                  className="w-full px-2.5 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 focus:outline-none"
                >
                  <option value="" disabled>
                    -- Select Preset (e.g. B.Tech Comp, MBA, B.Pharm) --
                  </option>
                  {STANDARD_PRESETS.map((sp) => (
                    <option key={sp.program_code} value={sp.program_code}>
                      {sp.name} ({sp.program_code}) - {sp.degree_level}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block font-medium text-slate-300 mb-1">Department *</label>
                <select
                  required
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                >
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">Program Code *</label>
                <input
                  type="text"
                  required
                  value={formData.program_code}
                  onChange={(e) => setFormData({ ...formData, program_code: e.target.value })}
                  placeholder="e.g. BTCE"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block font-medium text-slate-300 mb-1">Program Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. B.Tech Computer Engineering"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">Short Name</label>
                <input
                  type="text"
                  value={formData.short_name}
                  onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                  placeholder="e.g. B.Tech Comp"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <label className="block font-medium text-slate-300 mb-1">Degree Level *</label>
                <select
                  value={formData.degree_level}
                  onChange={(e) => setFormData({ ...formData, degree_level: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                >
                  {DEGREE_LEVELS.map((dl) => (
                    <option key={dl.value} value={dl.value}>
                      {dl.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">Category *</label>
                <select
                  value={formData.program_category}
                  onChange={(e) => setFormData({ ...formData, program_category: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                >
                  {PROGRAM_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">Academic Pattern Master *</label>
                <select
                  required
                  value={formData.academic_pattern_ref || ""}
                  onChange={(e) => {
                    const selectedP = academicPatterns.find((p) => p.id === e.target.value);
                    setFormData({
                      ...formData,
                      academic_pattern_ref: e.target.value,
                      academic_pattern: selectedP ? selectedP.pattern_type : formData.academic_pattern,
                      total_semesters: selectedP ? selectedP.semester_count : formData.total_semesters,
                      duration_years: selectedP ? selectedP.duration_years : formData.duration_years,
                    });
                  }}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                >
                  <option value="" disabled>-- Select Academic Pattern --</option>
                  {academicPatterns.map((pat) => (
                    <option key={pat.id} value={pat.id}>
                      {pat.name} ({pat.code}) - {pat.pattern_type} / {pat.credit_system}
                    </option>
                  ))}
                </select>
              </div>

            </div>

            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <label className="block font-medium text-slate-300 mb-1">Duration (Years)</label>
                <input
                  type="number"
                  min="1"
                  max="6"
                  value={formData.duration_years}
                  onChange={(e) => setFormData({ ...formData, duration_years: parseInt(e.target.value) || 3 })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">Total Semesters</label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={formData.total_semesters}
                  onChange={(e) => setFormData({ ...formData, total_semesters: parseInt(e.target.value) || 6 })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">Total Credits</label>
                <input
                  type="number"
                  value={formData.total_credits}
                  onChange={(e) => setFormData({ ...formData, total_credits: parseInt(e.target.value) || 120 })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block font-medium text-slate-300 mb-1">Approval Body</label>
                <input
                  type="text"
                  placeholder="e.g. AICTE / UGC / PCI"
                  value={formData.approval_body}
                  onChange={(e) => setFormData({ ...formData, approval_body: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">University</label>
                <select
                  value={formData.university}
                  onChange={(e) => setFormData({ ...formData, university: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
                >
                  <option value="">-- Select University --</option>
                  {universities.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="text-xs">
              <label className="block font-medium text-slate-300 mb-1">Description</label>
              <textarea
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Program details, objectives..."
                className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2 pt-1 text-xs">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-slate-800 bg-slate-950 text-indigo-600 focus:ring-0"
              />
              <label htmlFor="is_active" className="text-slate-300 font-medium">
                Active Program Status
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-slate-800 text-slate-300 hover:text-white text-xs font-semibold rounded-xl"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-lg disabled:opacity-50 transition-all"
              >
                {editingProgram ? "Update Program" : "Create Program"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingId && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 text-center">
            <XCircle className="w-10 h-10 text-rose-500 mx-auto" />
            <h3 className="text-sm font-bold text-slate-100">Delete Program?</h3>
            <p className="text-xs text-slate-400">
              Are you sure you want to delete this program? This action cannot be undone.
            </p>
            <div className="flex justify-center gap-3 pt-2">
              <button
                onClick={() => setDeletingId(null)}
                className="px-4 py-2 bg-slate-800 text-slate-300 text-xs font-semibold rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deletingId)}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded-xl shadow-lg"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
